// GS1 DataMatrix Encoder
// Handles encoding of GS1 Application Identifiers for DataMatrix barcodes

import { GS1ApplicationIdentifierValue } from '@/types'
import { getAIDefinition } from './applicationIdentifiers'

// GS (Group Separator) character - ASCII 29
// Used as FNC1 separator between variable-length AIs
const GS = String.fromCharCode(29)

// FNC1 representation for ZPL (in hex mode with ^FH)
const FNC1_ZPL = '_1'

// FNC1 representation for bwip-js (GS1 DataMatrix)
// bwip-js gs1datamatrix automatically handles FNC1
const FNC1_BWIP = GS

/**
 * Returns AIs in the user-defined order
 * The user controls the order via the UI - we respect their choice
 * Note: Fixed-length AIs don't need separators when followed by other AIs,
 * but we let the user decide the order for flexibility
 */
function sortAIsForEncoding(ais: GS1ApplicationIdentifierValue[]): GS1ApplicationIdentifierValue[] {
  // Return a copy without reordering - respect user's order
  return [...ais]
}

/**
 * Encodes GS1 data for bwip-js datamatrix with parsefnc option
 * bwip-js uses "^FNC1" to represent FNC1 character when parsefnc is enabled
 * Format: ^FNC10107891234567890172512311^FNC1ABC123
 * - ^FNC1 at start indicates GS1 DataMatrix
 * - ^FNC1 between AIs serves as separator for variable-length AIs
 */
export function encodeGS1ForBwipJs(
  ais: GS1ApplicationIdentifierValue[],
  variableData?: Record<string, string>
): string {
  if (ais.length === 0) return ''

  const sortedAIs = sortAIsForEncoding(ais)

  // Start with FNC1 to indicate GS1 format
  let result = '^FNC1'

  for (let i = 0; i < sortedAIs.length; i++) {
    const aiValue = sortedAIs[i]
    const definition = getAIDefinition(aiValue.ai)

    // Get the actual value (resolve variables if needed)
    let value = aiValue.value
    if (aiValue.isVariable && aiValue.variableName && variableData) {
      value = variableData[aiValue.variableName] || value
    }

    // Add AI code and value
    result += aiValue.ai + value

    // Add FNC1 separator after variable-length AIs (except for the last one)
    if (definition && !definition.fixedLength && i < sortedAIs.length - 1) {
      result += '^FNC1'
    }
  }

  return result
}

/**
 * Encodes GS1 data for ZPL ^BX command
 * For GS1 DataMatrix in ZPL with format ID 6 and escape char _ (underscore):
 * - _1 represents FNC1 (must be at the start for GS1)
 * - _d029 represents GS (Group Separator, ASCII 29) for variable-length AI separation
 */
export function encodeGS1ForZPL(
  ais: GS1ApplicationIdentifierValue[],
  variableData?: Record<string, string>
): string {
  if (ais.length === 0) return ''

  const sortedAIs = sortAIsForEncoding(ais)

  // Start with FNC1 to indicate GS1 format (using _ escape character)
  let result = '_1'

  for (let i = 0; i < sortedAIs.length; i++) {
    const aiValue = sortedAIs[i]
    const definition = getAIDefinition(aiValue.ai)

    // Get the actual value (resolve variables if needed)
    let value = aiValue.value
    if (aiValue.isVariable && aiValue.variableName && variableData) {
      value = variableData[aiValue.variableName] || value
    }

    // Add AI code and value
    result += aiValue.ai + value

    // Add GS separator after variable-length AIs (except for the last one)
    // Using _d029 for ASCII 29 (GS character)
    if (definition && !definition.fixedLength && i < sortedAIs.length - 1) {
      result += '_d029'
    }
  }

  return result
}

/**
 * Creates a human-readable representation of GS1 data
 * Example: (01)07891234567890(17)251231(10)ABC123
 */
export function formatGS1ForHumanReadable(
  ais: GS1ApplicationIdentifierValue[],
  variableData?: Record<string, string>
): string {
  return ais.map(aiValue => {
    let value = aiValue.value
    if (aiValue.isVariable && aiValue.variableName && variableData) {
      value = variableData[aiValue.variableName] || value
    }
    return `(${aiValue.ai})${value}`
  }).join('')
}

/**
 * Parses a human-readable GS1 string back to AI values
 * Example input: "(01)07891234567890(17)251231(10)ABC123"
 */
export function parseGS1String(gs1String: string): GS1ApplicationIdentifierValue[] {
  const result: GS1ApplicationIdentifierValue[] = []
  const regex = /\((\d{2,4})\)([^(]*)/g
  let match

  while ((match = regex.exec(gs1String)) !== null) {
    result.push({
      ai: match[1],
      value: match[2],
    })
  }

  return result
}

/**
 * Encodes GS1 data for bwip-js gs1-128 barcode
 * bwip-js gs1-128 uses parentheses format: (01)07891234567890(17)251231(10)ABC123
 * The library handles FNC1 and separators automatically
 */
export function encodeGS1ForBwipJsCode128(
  ais: GS1ApplicationIdentifierValue[],
  variableData?: Record<string, string>
): string {
  if (ais.length === 0) return ''

  const sortedAIs = sortAIsForEncoding(ais)

  let result = ''

  for (const aiValue of sortedAIs) {
    // Get the actual value (resolve variables if needed)
    let value = aiValue.value
    if (aiValue.isVariable && aiValue.variableName && variableData) {
      value = variableData[aiValue.variableName] || value
    }

    // Add AI code in parentheses and value
    result += `(${aiValue.ai})${value}`
  }

  return result
}

/**
 * Encodes GS1 data for ZPL ^BC (Code 128) command
 * For GS1-128 in ZPL:
 * - Use >; to enable Code 128 subset C (numeric pairs, more efficient)
 * - Use >8 to insert FNC1 character
 * - Data format: >8>;01>507891234567890>817251231>8ABC123
 * Where >8 is FNC1, >; switches to subset C, >5 switches to subset B
 */
export function encodeGS1ForZPLCode128(
  ais: GS1ApplicationIdentifierValue[],
  variableData?: Record<string, string>
): string {
  if (ais.length === 0) return ''

  const sortedAIs = sortAIsForEncoding(ais)

  // Start with FNC1 (>8) to indicate GS1-128
  let result = '>8'

  for (let i = 0; i < sortedAIs.length; i++) {
    const aiValue = sortedAIs[i]
    const definition = getAIDefinition(aiValue.ai)

    // Get the actual value (resolve variables if needed)
    let value = aiValue.value
    if (aiValue.isVariable && aiValue.variableName && variableData) {
      value = variableData[aiValue.variableName] || value
    }

    // Add AI code and value
    result += aiValue.ai + value

    // Add GS separator (>7 followed by 1D hex which is GS char) after variable-length AIs
    // In ZPL Code 128, we use the actual GS character for separation
    if (definition && !definition.fixedLength && i < sortedAIs.length - 1) {
      // Use FNC1 as separator (same as GS for GS1-128)
      result += '>8'
    }
  }

  return result
}
